'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, CreditCard } from 'lucide-react';
import Link from 'next/link';
import { client } from '../../utils/trpc';

// The Trons page points at Settings > Billing rather than embedding the form.
// Buying trons and arranging a recurring charge are different intents, and the
// full auto top-up card (package, threshold, two caps, four alert toggles) was
// most of the page's height for a setting almost nobody changes twice.
//
// It still reports the CURRENT state, because a link that only says "Auto
// top-up" answers nothing — the reason to look is to find out whether you are
// covered, and that answer belongs here rather than one navigation away.
const AutoTopUpLink = () => {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["autoTopUp"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data
  } = useQuery(t0);
  if (!data) {
    return null;
  }
  const {
    config,
    hasSavedCard
  } = data;
  const on = config?.enabled ?? false;
  let t1;
  if ($[1] !== config?.thresholdTrons || $[2] !== hasSavedCard || $[3] !== on) {
    t1 = on ? `On — tops up when you drop below ${Number(config?.thresholdTrons ?? 0).toLocaleString()} trons` : hasSavedCard ? "Off \u2014 a long task can stop halfway when you run out" : "Off \u2014 save a card on your next purchase to turn it on";
    $[1] = config?.thresholdTrons;
    $[2] = hasSavedCard;
    $[3] = on;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const status = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CreditCard className="shrink-0 text-textSecondary" height={18} width={18} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-sm font-emphasis text-textPrimary">Trons auto top-up</span>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `truncate text-xs ${on ? "text-textSecondary" : "text-amber-400"}`;
  let t5;
  if ($[7] !== status || $[8] !== t4) {
    t5 = <span className="flex min-w-0 flex-col">{t3}<span className={t4}>{status}</span></span>;
    $[7] = status;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronRight className="ml-auto shrink-0 text-textSecondary" height={18} width={18} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = <Link href="/settings/billing" className="hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-xl border border-solid border-ds-border-standard bg-background-secondary p-4 transition-colors">{t2}{t5}{t6}</Link>;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
export default AutoTopUpLink;
function _temp() {
  return unwrapFromResult(client.autoTopUp.get.query());
}